/**
 * Date Helper Utilities
 * Extracted from App.js as part of Phase 1 refactoring.
 * 
 * These are pure functions for date formatting and parsing.
 * Used throughout the application for consistent date handling.
 */

/**
 * Format a date for use in filenames (MM-DD-YYYY)
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateForFilename = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}-${d.getFullYear()}`;
};

/**
 * Format a YYYY-MM-DD date string for display without timezone conversion.
 * This prevents the common bug where "2026-01-12" becomes "1/11/2026" due to UTC interpretation.
 * @param {string} dateString - Date string to format
 * @returns {string} Formatted date for display (M/D/YYYY)
 */
export const formatDateStringForDisplay = (dateString) => {
  if (!dateString) return 'N/A';
  
  // Handle 'unknown' date from legacy imports
  if (dateString === 'unknown' || dateString.toLowerCase() === 'unknown') {
    return 'No Date';
  }

  // Jun 2026 fix — authorizations / claim DOS / and similar calendar-
  // date fields are stored by the backend as ``datetime`` (UTC-midnight
  // ISO strings like ``"2026-06-27T00:00:00+00:00"``).  Calling
  // ``new Date(...).toLocaleDateString()`` on that value UTC-shifts a
  // negative-offset viewer back into the previous day (June 26 in
  // Eastern), reintroducing the off-by-one display bug we thought we'd
  // closed.  Calendar-date semantics are signalled by a ``T00:00:00``
  // time portion — extract the YYYY-MM-DD prefix directly and skip the
  // Date constructor entirely.  Real timestamps (``T13:45:00Z`` etc.)
  // still fall through to the UTC→local conversion branch below.
  const calendarMatch = String(dateString).match(/^(\d{4})-(\d{2})-(\d{2})T00:00:00/);
  if (calendarMatch) {
    const [, year, month, day] = calendarMatch;
    return `${month}/${day}/${year}`;
  }

  // Check if it's a full ISO timestamp with time component (contains 'T')
  if (String(dateString).includes('T')) {
    // Parse as Date to convert UTC to local time
    const date = new Date(dateString);
    // Check for invalid date
    if (isNaN(date.getTime())) return 'No Date';
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  }
  
  // If it's already in YYYY-MM-DD format (date only), parse it directly without timezone conversion
  // This prevents dates like "2026-02-27" from shifting days due to UTC interpretation
  const match = String(dateString).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const [, year, month, day] = match;
    return `${month}/${day}/${year}`;
  }
  
  // Fallback to Date parsing for other formats
  const parsed = new Date(dateString);
  // Check for invalid date
  if (isNaN(parsed.getTime())) return 'No Date';
  return parsed.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

/**
 * Convert a stored date value into a YYYY-MM-DD string suitable for a
 * <input type="date"> value WITHOUT a timezone shift.
 *
 * Mar 2026 — fixes authorization start/end dates appearing in UTC on
 * edit-form prefill (e.g. "2026-01-15" stored → loaded as UTC midnight
 * → re-stringified to local timezone → off by one day in negative
 * timezones).
 *
 * Strategy: if the input is already YYYY-MM-DD (or YYYY-MM-DDT...),
 * take the first 10 characters directly.  Otherwise fall back to
 * Date parsing.  Returns '' for null/invalid inputs (matches the
 * default empty state for <input type=date>).
 */
export const toDateInputValue = (dateString) => {
  if (!dateString) return '';
  // Accept Date instances too — emits YYYY-MM-DD in the LOCAL timezone
  // (caller usually wants "today as the user sees it", not UTC today).
  if (dateString instanceof Date) {
    if (isNaN(dateString.getTime())) return '';
    const y = dateString.getFullYear();
    const m = String(dateString.getMonth() + 1).padStart(2, '0');
    const day = String(dateString.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
  const s = String(dateString);
  // YYYY-MM-DD prefix — works for "2026-01-15" AND "2026-01-15T22:00:00-05:00"
  // (we want the LOCAL stored date, not its UTC equivalent).
  const match = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  // Fallback for unusual formats.
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  // Use locale parts so we don't shift across midnight UTC.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/**
 * Get today's date in local timezone as YYYY-MM-DD string.
 * This prevents the UTC conversion bug where new Date().toISOString() shows tomorrow's date
 * when it's evening in the user's timezone.
 * @returns {string} Today's date as YYYY-MM-DD
 */
export const getTodayLocalDate = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

/**
 * Parse UTC timestamps from backend.
 * Backend returns timestamps without 'Z' suffix (e.g., "2026-01-24T04:16:54.849000")
 * Without 'Z', some browsers treat this as local time instead of UTC.
 * This function ensures consistent UTC parsing across all browsers.
 * @param {string} timestamp - UTC timestamp from backend
 * @returns {Date|null} Parsed Date object or null
 */
export const parseUTCTimestamp = (timestamp) => {
  if (!timestamp) return null;
  // If timestamp doesn't end with 'Z' and looks like ISO format, append 'Z'
  const str = String(timestamp);
  if (!str.endsWith('Z') && str.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
    return new Date(str + 'Z');
  }
  return new Date(str);
};

/**
 * Format a UTC timestamp to local time string for messages/logs.
 * @param {string} timestamp - UTC timestamp
 * @param {Object} options - Intl.DateTimeFormat options override
 * @returns {string} Formatted local time string
 */
export const formatMessageTimestamp = (timestamp, options = {}) => {
  const date = parseUTCTimestamp(timestamp);
  if (!date || isNaN(date.getTime())) return 'N/A';
  const defaultOptions = {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  };
  return date.toLocaleString('en-US', { ...defaultOptions, ...options });
};

/**
 * Format a date as relative time ("just now", "2 mins ago", etc.)
 * Used for "Last saved" indicators.
 * @param {Date} date - Date to format
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date) => {
  if (!date) return '';
  
  const now = new Date();
  const diff = Math.floor((now - date) / 1000); // Difference in seconds
  
  if (diff < 10) return 'just now';
  if (diff < 60) return `${diff} secs ago`;
  if (diff < 120) return '1 min ago';
  if (diff < 3600) return `${Math.floor(diff / 60)} mins ago`;
  if (diff < 7200) return '1 hour ago';
  if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};
